import { format_ist } from './care_request_rules';

// resolve step: who asked (Care vs the teacher's / system's record) and which move a Care note
// belongs to. The server decides the states (goserver carereq / changehistory); these helpers only
// word them for Ops.

export const STATE_DISAGREEMENT = 'DISAGREEMENT';
export const STATE_RESOLVED = 'RESOLVED';
export const MATCH_UNCLEAR = 'UNCLEAR';
export const MATCH_CONFIRMED = 'CONFIRMED';

const ANSWER_LABEL = { STUDENT: 'Student', TEACHER: 'Teacher' };
const SOURCE_LABEL = {
  TEACHER_APP_ANSWER: "Teacher's answer in the teacher app",
  TEACHER_APP_MOVE: 'Teacher moved the class in the teacher app',
  SESSION_CANCEL_FLAGS: "Session's cancellation details"
};

export const answer_label = (v) => ANSWER_LABEL[v] || v || '--';

// The answer reports show (the final one when resolved, else Care's), with the marker and its detail line.
export function who_asked_view(who_asked, fallback_value) {
  const wa = who_asked && typeof who_asked === 'object' ? who_asked : null;
  const shown = answer_label((wa && wa.shown) || fallback_value);
  if (wa && wa.state === STATE_RESOLVED && wa.resolution) {
    const r = wa.resolution;
    return {
      shown,
      marker: 'resolved',
      marker_text: 'Resolved',
      detail: `Final answer set by ${r.resolved_by_name || 'Unknown user'} · ${format_ist(r.resolved_date)} · Reason: ${r.resolution_reason}`
    };
  }
  if (wa && wa.state === STATE_DISAGREEMENT) {
    return {
      shown,
      marker: 'disagreement',
      marker_text: 'Disagreement — needs resolve',
      detail: `Care: ${answer_label(wa.care_value)} · ${SOURCE_LABEL[wa.other_source] || 'Other record'}: ${answer_label(wa.other_value)}`
    };
  }
  return { shown, marker: '', marker_text: '', detail: '' };
}

// A row of the apis reschedule log (get_session_time_change_history) as a who_asked block; rows from an apis without
// the Care columns fall back to the row's own reschedule_requested_by.
export function reschedule_log_view(log) {
  if (!log || !log.who_asked_state) return who_asked_view(null, log && log.reschedule_requested_by);
  return who_asked_view(
    {
      state: log.who_asked_state,
      shown: log.who_asked,
      care_value: log.care_initiated_by,
      other_value: log.teacher_answer,
      other_source: log.reschedule_requested_by ? 'TEACHER_APP_ANSWER' : 'TEACHER_APP_MOVE',
      resolution: log.resolution
    },
    log.reschedule_requested_by
  );
}

// The note's move: unclear (2+ moves in the time window) or picked by Ops.
export function match_view(match, kind_label = 'move') {
  if (!match || typeof match !== 'object') return { marker: '', marker_text: '' };
  if (match.state === MATCH_UNCLEAR) {
    return {
      marker: 'disagreement',
      marker_text: `Match unclear — check which ${kind_label} this note belongs to`
    };
  }
  if (match.state === MATCH_CONFIRMED) return { marker: 'resolved', marker_text: 'Move confirmed' };
  return { marker: '', marker_text: '' };
}

const answer_open = (wa) =>
  !!wa && (wa.state === STATE_DISAGREEMENT || wa.state === STATE_RESOLVED);
const match_open = (m) => !!m && (m.state === MATCH_UNCLEAR || m.state === MATCH_CONFIRMED);

// Which parts the resolve dialog offers; nothing to offer = no Resolve button.
export function resolve_parts(who_asked, match, can_resolve) {
  if (!can_resolve) return { answer: false, match: false };
  return { answer: answer_open(who_asked), match: match_open(match) };
}

export function new_resolve_draft(who_asked) {
  const r = (who_asked && who_asked.resolution) || {};
  return {
    resolved_value: r.resolved_value || '',
    matched_audit_id: r.matched_audit_id || '',
    reason: ''
  };
}

export function validate_resolve_draft(draft, parts) {
  const errors = {};
  const chose_answer = parts.answer && !!draft.resolved_value;
  const chose_match = parts.match && !!draft.matched_audit_id;
  if (!chose_answer && !chose_match) {
    errors.choice =
      parts.match && !parts.answer
        ? 'Choose the move this note belongs to.'
        : 'Choose the final answer.';
  }
  if ((draft.reason || '').trim().length < 3) {
    errors.reason = 'A reason (at least 3 characters) is required.';
  }
  return errors;
}

// Only the parts the dialog offered are sent; the server keeps any part not sent.
export function build_resolve_payload(draft, parts) {
  const body = { resolution_reason: draft.reason.trim() };
  if (parts.answer && draft.resolved_value) body.resolved_value = draft.resolved_value;
  if (parts.match && draft.matched_audit_id) body.matched_audit_id = Number(draft.matched_audit_id);
  return body;
}
